import React, { useState, useEffect, useRef } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from "primereact/progressspinner";
import "./IncompleteContracts.css";
import apiService from "../utils/api";
import Snackbar from '@mui/material/Snackbar';
import Breadcrumbs from "../components/Breadcrumbs";
import { useRBAC } from "./rbac/RbacProvider";
import { useNavigate } from "react-router-dom";
import Loader from "./Loader";
import Footer from './Footer';
import Sidebar from "./sidebar/Sidebar";
import SubPage from "./SubPage";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

import { Edit } from "@mui/icons-material";



// Custom hook: perform auth token handling and user-details fetch
const useAuthToken = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");

    if (accessToken && refreshToken) {
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("refresh_token", refreshToken);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const token = localStorage.getItem("access_token");
    if (!token) {
      console.warn("No access token found, redirecting to login");
      window.location.href = "/login";
      return;
    }

    const fetchUserDetails = async () => {
      try {
        const userRes = await apiService.fetchData("user-details");
        if (userRes?.status === "success" && userRes.user) {
          sessionStorage.setItem("user_id", userRes.user.user_id);
          sessionStorage.setItem(
            "user_role",
            userRes.user.role?.[0]?.RoleName || userRes.user.role || ""
          );
          sessionStorage.setItem("user_name", userRes.user.user_name);
          sessionStorage.setItem("user_emailId", userRes.user.email);
        } else {
          throw new Error("Invalid token");
        }
        setReady(true);
      } catch (err) {
        console.error("Auth validation failed:", err);
        localStorage.clear();
        window.location.href = "/login";
      }
    };

    fetchUserDetails();
  }, []);

  return ready;
};

// Helper to produce readable labels for column keys
const getColumnLabel = (key) => {
  switch (key) {
    case 'name': return 'Contract Name';
    case 'type': return 'Type';
    case 'createdDate': return 'Created';
    case 'contractType': return 'Contract Type';
    case 'creationType': return 'Creation Type';
    case 'vendor': return 'Vendor';
    default: return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
  }
};

// Small component to render a single column option row (keeps mapping simple)
const ColumnOptionItem = ({ keyName, tempVisibleColumns, mandatoryColumns, toggleColumn }) => {
  const label = getColumnLabel(keyName);
  const isMandatory = mandatoryColumns.includes(keyName);
  return (
    <div key={keyName} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
      <span style={{ fontSize: "14px", color: "#111827" }}>
        {label}{isMandatory ? <span style={{ color: '#ff6b35' }}> *</span> : ''}
      </span>
      <button onClick={() => toggleColumn(keyName)} disabled={isMandatory} aria-label={tempVisibleColumns[keyName] ? `Hide ${keyName}` : `Show ${keyName}`} style={{ background: "transparent", border: "none", cursor: isMandatory ? 'not-allowed' : 'pointer', opacity: isMandatory ? 0.6 : 1 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
          {!tempVisibleColumns[keyName] && <line x1="2" y1="2" x2="18" y2="18" stroke="#000000" strokeWidth="3" />}
        </svg>
      </button>
    </div>
  );
};

const IncompleteContracts = () => {
  const tokenReady = useAuthToken();
  const { role } = useRBAC();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ global: "" });
  const [loading, setLoading] = useState(true);
  const [loaderMessage, setLoaderMessage] = useState("Loading incomplete contracts...");
  const [snackbar, setSnackbar] = useState({ open: false, success: false, message: "" });
  const [flatData, setFlatData] = useState([]);
  const [filteredFlatData, setFilteredFlatData] = useState([]);
  const [clientFilter, setClientFilter] = useState('');
  const [contractTypeFilter, setContractTypeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [creationTypeFilter, setCreationTypeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusOptions, setStatusOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [contractTypeOptions, setContractTypeOptions] = useState([]);
  const [creationTypeOptions, setCreationTypeOptions] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const savedColumns = localStorage.getItem('incompleteContractsVisibleColumns');
    const defaultColumns = {
      name: true,
      type: true,
      startDate: true,
      createdDate: false,
      status: true,
      modified: false,
      contractType: true,
      creationType: true,
      vendor: true,
    };
    return savedColumns ? JSON.parse(savedColumns) : defaultColumns;
  });
  const [selectedNode, setSelectedNode] = useState(null);
  const [activeSection, setActiveSection] = useState('incompleteContracts');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [tempVisibleColumns, setTempVisibleColumns] = useState(visibleColumns);
  const downloadRef = useRef(null);
  const mandatoryColumns = ['name', 'type', 'status', 'vendor'];

  const [showMoveDropdown, setShowMoveDropdown] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isModalMaximized, setIsModalMaximized] = useState(false);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [showYearHierarchy, setShowYearHierarchy] = useState(false);
  const [expandedYears, setExpandedYears] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const yearDropdownRef = useRef(null);

  const capitalize = (s) => (typeof s === 'string' && s.length > 0) ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

  const extractYearFromTitle = (startDate) => {
    if (!startDate || startDate === 'N/A') return 'Unknown Year';
    const year = new Date(startDate).getFullYear();
    return isNaN(year) ? 'Unknown Year' : year.toString();
  };

  const groupContractsByYear = (contracts) => {
    const grouped = {};
    contracts.forEach(contract => {
      const year = extractYearFromTitle(contract.startDate);
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(contract);
    });
    return grouped;
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  const getUserId = () => {
    return Number(sessionStorage.getItem("user_id")) || null;
  };

  const getUserRole = () => (sessionStorage.getItem("user_role") || "").replace(/\s+/g, '');

  const getApiEndpoint = () => {
    const rawRole = sessionStorage.getItem("user_role") || "";
    const role = rawRole.replace(/\s+/g, ''); 
    const userId = getUserId();

    if (role === "Admin") {
      return "all-contracts-hierarchy?is_wip=false";
    }

    if (role === "ClientPartners") { 
      return `all-contracts-hierarchy?is_wip=false&client_partner=true&partner_id=${userId}`;
    }

    return `contract-hierarchy?user_id=${userId}&is_wip=false`;
  };

  const checkMissingFields = (contract) => {
    const requiredFields = ['client_name', 'start_date', 'end_date'];
    const missingFields = [];
    
    requiredFields.forEach(field => {
      if (!contract[field] || contract[field] === 'N/A' || contract[field] === '') {
        missingFields.push(field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()));
      }
    });
    
    return missingFields;
  };

  const fetchData = async () => {
    try {
      setLoaderMessage("Loading incomplete contracts...");
      setLoading(true);
      const userId = getUserId();

      const flatRes = await apiService.fetchData(`user-flat-contracts?defaulter=true`);

      if (flatRes && flatRes.status === "success" && Array.isArray(flatRes.data)) {
        const contracts = flatRes.data;

        const flatDataMapped = contracts.map((item) => {
          return {
            id: `CONTRACT-${item.link_square_uuid}`,
            name: item.contract_title || "N/A",
            clientName: item.client_id && !item.vendor_id ? item.client_id : "",
            vendorName: item.vendor_id && !item.client_id ? item.vendor_id : "",
            type: item.client_id === null ? "Vendor" : "Client",
            createdDate: item.created_at ? new Date(item.created_at).toISOString().split("T")[0] : "N/A",
            status: item.status_id || "Signed",
            modified: item.updated_at ? new Date(item.updated_at).toISOString().split("T")[0] : "N/A",
            startDate: item.start_date && item.start_date !== "N/A" ? (() => {
              const date = new Date(item.start_date);
              return isNaN(date.getTime()) ? "N/A" : date.toISOString().split("T")[0];
            })() : "N/A",
            contractType: item.contract_type || "N/A",
            creationType: capitalize(item.creation_type) || "N/A",
            clientKey: item.client_id || "N/A",
            link_square_uuid: item.link_square_uuid,
          };
        });

        setFlatData(flatDataMapped);
        setFilteredFlatData(flatDataMapped);

        // Derive filter options
        const uniqueClients = [...new Set(contracts.map((item) => item.client_id || "Not Specified"))].filter(Boolean);
        const uniqueContractTypes = [...new Set(contracts.map((item) => item.contract_type))].filter(Boolean);
        const uniqueStatuses = [...new Set(contracts.map((item) => item.status_id || "Draft"))].filter(Boolean);
        const uniqueCreationTypes = [...new Set(contracts.map((item) => item.creation_type))].filter(Boolean);

        setClientOptions(uniqueClients);
        setContractTypeOptions(uniqueContractTypes);
        setStatusOptions(uniqueStatuses);
        setCreationTypeOptions(uniqueCreationTypes.map(capitalize));
      } else {
        throw new Error("Invalid contracts response");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setSnackbar({
        open: true,
        success: false,
        message: "Error fetching contract data",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle edit click: extracted to avoid deep nesting in JSX
  const handleEditClick = async (rowData, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const cleanId = (rowData.id || '').replace('CONTRACT-', '');
    const linkSquareUuid = rowData.link_square_uuid || cleanId;

    setEditModalOpen(true);
    setEditModalLoading(true);
    try {
      // Prefer requesting by uuid if available
      const response = await apiService.fetchData(`user-flat-contracts?defaulter=true&link_square_uuid=${linkSquareUuid}`);
      if (response && response.status === 'success' && Array.isArray(response.data) && response.data.length > 0) {
        const fullContract = response.data[0];
        setEditingContract(fullContract);
        setEditFormData(fullContract);
        return;
      }

      // Fallback: fetch list and find by uuid
      const fallback = await apiService.fetchData(`user-flat-contracts?defaulter=true`);
      if (fallback && fallback.status === 'success' && Array.isArray(fallback.data)) {
        const fullContract = fallback.data.find(item => item.link_square_uuid === cleanId || item.link_square_uuid === linkSquareUuid);
        if (fullContract) {
          setEditingContract(fullContract);
          setEditFormData(fullContract);
          return;
        }
      }
      // If not found, close modal and show error
      setSnackbar({ open: true, success: false, message: 'Contract details not found' });
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error fetching contract details:', error);
      setSnackbar({ open: true, success: false, message: 'Error loading contract details' });
      setEditModalOpen(false);
    } finally {
      setEditModalLoading(false);
    }
  };

  // Action body renderer for DataTable action column
  const actionBodyTemplate = (rowData) => (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
      <Edit
        style={{ cursor: 'pointer', color: '#ff6b35', width: '16px', height: '16px' }}
        titleAccess="Edit Contract"
        onClick={(e) => handleEditClick(rowData, e)}
      />
      <div style={{ position: 'relative' }}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#ff6b35"
          strokeWidth="2"
          style={{ cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); setShowMoveDropdown(showMoveDropdown === rowData.id ? null : rowData.id); }}
          titleAccess="Move Contract"
        >
          <path d="M3 12h18m-9-9l9 9-9 9" />
        </svg>
        {showMoveDropdown === rowData.id && (
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '0',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              zIndex: 10,
              minWidth: '120px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => { handleMoveContract(rowData, 'incomplete'); setShowMoveDropdown(null); }} style={{ display: 'block', width: '100%', padding: '8px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px' }}>Incomplete</button>
            <button onClick={() => { handleMoveContract(rowData, 'signed'); setShowMoveDropdown(null); }} style={{ display: 'block', width: '100%', padding: '8px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px' }}>Signed</button>
          </div>
        )}
      </div>
    </div>
  );

  // Render a single year group block (extracted for clarity)
  const renderYearGroup = (year, contracts) => (
    <div key={year} style={{ borderBottom: '1px solid #f0f0f0' }}>
      <div
        onClick={() => toggleYear(year)}
        style={{
          padding: '12px 16px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: '#f8f9fa',
          fontWeight: 'bold',
          color: '#ff6b35'
        }}
      >
        <span style={{ fontSize: '16px' }}>{expandedYears[year] ? '📂' : '📁'}</span>
        <span>{year} ({contracts.length} contracts)</span>
      </div>
      {expandedYears[year] && (
        <DataTable
          value={contracts}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          tableStyle={{ width: '100%' }}
          emptyMessage="No contracts found"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        >
          {visibleColumns.name && (<Column field="name" header="Contract Name" body={nameTemplate} sortable />)}
          <Column field="clientName" header="Client" sortable />
          {visibleColumns.vendor && (<Column field="vendorName" header="Vendor" sortable />)}
          {visibleColumns.contractType && (<Column field="contractType" header="Contract Type" sortable />)}
          {visibleColumns.status && (<Column field="status" header="Status" sortable />)}
          {visibleColumns.startDate && (<Column field="startDate" header="Start Date" sortable />)}
          {visibleColumns.createdDate && (<Column field="createdDate" header="Created Date" sortable />)}
          <Column header="Actions" style={{ width: '120px', textAlign: 'center' }} body={actionBodyTemplate} />
        </DataTable>
      )}
    </div>
  );

  // Helper: Render modal header
  const renderModalHeader = () => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", fontWeight: "600", fontSize: "18px", background: "linear-gradient(135deg, #ff6b35 0%, #ff8c61 100%)", color: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
        <span>Edit Contract</span>
      </div>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <button onClick={() => setIsModalMaximized(!isModalMaximized)} style={{ background: "transparent", border: "none", fontSize: "20px", cursor: "pointer", color: "#fff", padding: "4px 8px", borderRadius: "4px", transition: "background 0.2s" }} onMouseEnter={(e) => e.target.style.background = "rgba(255,255,255,0.2)"} onMouseLeave={(e) => e.target.style.background = "transparent"}>
          {isModalMaximized ? "⊡" : "□"}
        </button>
        <button onClick={() => { setEditModalOpen(false); setIsModalMaximized(false); setEditModalLoading(false); }} style={{ background: "transparent", border: "none", fontSize: "24px", cursor: "pointer", color: "#fff", padding: "4px 8px", borderRadius: "4px", transition: "background 0.2s" }} onMouseEnter={(e) => e.target.style.background = "rgba(255,255,255,0.2)"} onMouseLeave={(e) => e.target.style.background = "transparent"}>×</button>
      </div>
    </div>
  );

  // Helper: Render modal body content
  const renderModalBody = () => {
    if (editModalLoading) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "300px", gap: "16px" }}>
          <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="4" fill="transparent" animationDuration=".8s" />
          <p style={{ color: "#6b7280", fontSize: "14px" }}>Loading contract details...</p>
        </div>
      );
    }
    if (editingContract) {
      return (
        <div style={{ display: "grid", gridTemplateColumns: isModalMaximized ? "1fr 1fr 1fr" : "1fr 1fr", gap: "20px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Contract Title</label>
                  <input type="text" value={editFormData.contract_title || ""} onChange={(e) => setEditFormData({...editFormData, contract_title: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Contract Type</label>
                  <input type="text" value={editFormData.contract_type || ""} onChange={(e) => setEditFormData({...editFormData, contract_type: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Client Name</label>
                  <input type="text" value={editFormData.client_id || ""} onChange={(e) => setEditFormData({...editFormData, client_id: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Start Date</label>
                  <input type="date" value={editFormData.start_date || ""} onChange={(e) => setEditFormData({...editFormData, start_date: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>End Date</label>
                  <input type="date" value={editFormData.end_date === "Not Present" ? "" : editFormData.end_date || ""} onChange={(e) => setEditFormData({...editFormData, end_date: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Renewal Date</label>
                  <input type="date" value={editFormData.renewal_date === "Not Present" ? "" : editFormData.renewal_date || ""} onChange={(e) => setEditFormData({...editFormData, renewal_date: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Billing Country</label>
                  <input type="text" value={editFormData.billing_country || ""} onChange={(e) => setEditFormData({...editFormData, billing_country: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Billing Type</label>
                  <input type="text" value={editFormData.billing_type || ""} onChange={(e) => setEditFormData({...editFormData, billing_type: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Pay Rate Currency</label>
                  <input type="text" value={editFormData.pay_rate_currency || ""} onChange={(e) => setEditFormData({...editFormData, pay_rate_currency: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Net Term</label>
                  <input type="text" value={editFormData.net_term || ""} onChange={(e) => setEditFormData({...editFormData, net_term: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>PO Number</label>
                  <input type="text" value={editFormData.po_no || ""} onChange={(e) => setEditFormData({...editFormData, po_no: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Sales Representative</label>
                  <input type="text" value={editFormData.sales_representative_id || ""} onChange={(e) => setEditFormData({...editFormData, sales_representative_id: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Tags</label>
                  <input type="text" value={editFormData.tags || ""} onChange={(e) => setEditFormData({...editFormData, tags: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", fontSize: "13px", marginBottom: "6px", color: "#374151", fontWeight: "500" }}>Description</label>
                  <textarea value={editFormData.description || ""} onChange={(e) => setEditFormData({...editFormData, description: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", minHeight: "100px", resize: "vertical", transition: "border 0.2s", outline: "none" }} onFocus={(e) => e.target.style.borderColor = "#ff6b35"} onBlur={(e) => e.target.style.borderColor = "#d1d5db"} />
                </div>
              </div>
        );
    }
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "300px", color: "#6b7280" }}>
        <p>No contract data available</p>
      </div>
    );
  };

  // Helper: Render modal footer with action buttons
  const renderModalFooter = () => (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", padding: "20px 24px", borderTop: "1px solid #e5e7eb", background: "#fff" }}>
      <button onClick={() => { setEditModalOpen(false); setIsModalMaximized(false); setEditModalLoading(false); }} style={{ background: "#fff", color: "#6b7280", border: "1px solid #d1d5db", padding: "10px 20px", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: "500", transition: "all 0.2s" }} onMouseEnter={(e) => { e.target.style.background = "#f3f4f6"; e.target.style.borderColor = "#9ca3af"; }} onMouseLeave={(e) => { e.target.style.background = "#fff"; e.target.style.borderColor = "#d1d5db"; }}>Cancel</button>
      <button onClick={async () => {
        try {
          const updatedFields = {};
          Object.keys(editFormData).forEach(key => {
            if (editingContract[key] !== editFormData[key]) {
              updatedFields[key] = editFormData[key];
            }
          });
          if (Object.keys(updatedFields).length === 0) {
            setSnackbar({ open: true, success: false, message: "No changes to save" });
            return;
          }
          setLoaderMessage("Saving Contract");
          setLoading(true);
          const response = await apiService.putData(`user-flat-contracts/${editingContract.link_square_uuid}`, updatedFields);
          if (response && response.status === 'success') {
            setLoading(false);
            setLoaderMessage("Loading incomplete contracts...");
            setSnackbar({ open: true, success: true, message: "Contract saved" });
            setEditModalOpen(false);
            await fetchData();
          } else {
            setLoading(false);
            setLoaderMessage("Loading incomplete contracts...");
            throw new Error(response?.message || 'Update failed');
          }
        } catch (error) {
          console.error("Error updating contract:", error);
          setLoading(false);
          setLoaderMessage("Loading incomplete contracts...");
          setSnackbar({ open: true, success: false, message: error.message || "Error updating contract" });
        }
      }} style={{ background: "linear-gradient(135deg, #ff6b35 0%, #ff8c61 100%)", color: "#fff", border: "none", padding: "10px 24px", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: "600", boxShadow: "0 2px 8px rgba(255,107,53,0.3)", transition: "all 0.2s" }} onMouseEnter={(e) => e.target.style.transform = "translateY(-1px)"} onMouseLeave={(e) => e.target.style.transform = "translateY(0)"}>Save</button>
    </div>
  );

  // Main: Render edit modal wrapper
  const renderEditModal = () => {
    if (!editModalOpen) return null;
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
        <div style={{ width: isModalMaximized ? "90vw" : "700px", maxHeight: isModalMaximized ? "90vh" : "85vh", background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", transition: "all 0.3s ease" }}>
          {renderModalHeader()}
          <div style={{ maxHeight: isModalMaximized ? "calc(90vh - 140px)" : "calc(85vh - 140px)", overflowY: "auto", padding: "28px 32px", background: "#f9fafb" }}>
            {renderModalBody()}
          </div>
          {renderModalFooter()}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (tokenReady) {
      fetchData();
    }
  }, [tokenReady, role]);

  useEffect(() => {
    localStorage.setItem('incompleteContractsVisibleColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    if (showColumnOptions) {
      setTempVisibleColumns(visibleColumns);
    }
  }, [showColumnOptions, visibleColumns]);

  useEffect(() => {
    if (showColumnOptions) {
      let temp = { ...visibleColumns };
      mandatoryColumns.forEach(col => {
        if (temp.hasOwnProperty(col)) temp[col] = true;
      });
      setTempVisibleColumns(temp);
    }
  }, [showColumnOptions, visibleColumns]);

  useEffect(() => {
    let filtered = flatData;
    if (clientFilter) {
      filtered = filtered.filter(item => item.clientName.toLowerCase().includes(clientFilter.toLowerCase()));
    }
    if (contractTypeFilter) {
      filtered = filtered.filter(item => item.contractType.toLowerCase().includes(contractTypeFilter.toLowerCase()));
    }
    if (nameFilter) {
      filtered = filtered.filter(item => item.name.toLowerCase().includes(nameFilter.toLowerCase()));
    }
    if (creationTypeFilter) {
      filtered = filtered.filter((item) => item.creationType === creationTypeFilter);
    }
    if (statusFilter) {
      filtered = filtered.filter(item => item.status.toLowerCase().includes(statusFilter.toLowerCase()));
    }
    if (dateFilter) {
      filtered = filtered.filter(item =>
        item.createdDate.toLowerCase().includes(dateFilter.toLowerCase()) ||
        item.modified.toLowerCase().includes(dateFilter.toLowerCase())
      );
    }
    setFilteredFlatData(filtered);
  }, [clientFilter, contractTypeFilter, nameFilter, statusFilter, flatData, dateFilter, creationTypeFilter]);

  useEffect(() => {
    const searchValue = (filters.global || "").toLowerCase().trim();

    if (!searchValue) {
      setFilteredFlatData(flatData);
      return;
    }

    const filteredFlat = flatData.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(searchValue)
    );
    setFilteredFlatData(filteredFlat);
  }, [filters.global, flatData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      setShowMoveDropdown(null);
    };
    
    if (showMoveDropdown) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMoveDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setShowYearDropdown(false);
      }
    };
    
    if (showYearDropdown) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showYearDropdown]);

  const nameTemplate = (rowData) => (
    <div 
      style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#ff6b35' }}
      onClick={() => handleContractClick(rowData)}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff6b35" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <circle cx="12" cy="15" r="3" fill="#ff6b35"/>
        <path d="M12 12v6" stroke="white" strokeWidth="1"/>
      </svg>
      <span style={{ textDecoration: 'underline' }}>{rowData.name}</span>
    </div>
  );

  const handleContractClick = (rowData) => {
    setSelectedNode({
      id: rowData.id,
      data: {
        type: 'File',
        name: rowData.name,
        clientName: rowData.clientName,
        status: rowData.status,
        ...rowData
      }
    });
  };

  const toggleColumn = (key) => {
    if (mandatoryColumns.includes(key)) return;
    setTempVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const exportToExcel = () => {
    if (filteredFlatData.length === 0) {
      setSnackbar({ open: true, success: false, message: "No data available to download" });
      setShowDownloadMenu(false);
      return;
    }
    const rows = filteredFlatData.map((item) => {
      const row = {};
      if (visibleColumns.name) row["Contract Name"] = item.name || "";
      if (visibleColumns.type) row["Type"] = item.type || "";
      row["Client"] = item.clientName || "";
      if (visibleColumns.startDate) row["Start Date"] = item.startDate || "";
      if (visibleColumns.endDate) row["End Date"] = item.endDate || "";
      if (visibleColumns.renewalDate) row["Renewal Date"] = item.renewalDate || "";
      if (visibleColumns.createdDate) row["Created"] = item.createdDate || "";
      if (visibleColumns.status) row["Status"] = item.status || "";
      if (visibleColumns.modified) row["Modified"] = item.modified || "";
      if (visibleColumns.contractType) row["Contract Type"] = item.contractType || "";
      if (visibleColumns.creationType) row["Creation Type"] = item.creationType || "";
      return row;
    });
    const XLSX = require("xlsx");
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Incomplete Contracts");
    XLSX.writeFile(workbook, "incomplete_contracts.xlsx");
    setShowDownloadMenu(false);
  };

  const exportToPDF = () => {
    if (filteredFlatData.length === 0) {
      setSnackbar({ open: true, success: false, message: "No data available to download" });
      setShowDownloadMenu(false);
      return;
    }
    const jsPDF = require("jspdf");
    require("jspdf-autotable");
    const doc = new jsPDF();
    const headers = [];
    if (visibleColumns.name) headers.push("Contract Name");
    if (visibleColumns.type) headers.push("Type");
    headers.push("Client");
    if (visibleColumns.startDate) headers.push("Start Date");
    if (visibleColumns.endDate) headers.push("End Date");
    if (visibleColumns.renewalDate) headers.push("Renewal Date");
    if (visibleColumns.createdDate) headers.push("Created");
    if (visibleColumns.status) headers.push("Status");
    if (visibleColumns.modified) headers.push("Modified");
    if (visibleColumns.contractType) headers.push("Contract Type");
    if (visibleColumns.creationType) headers.push("Creation Type");
    const rows = filteredFlatData.map((item) => {
      const row = [];
      if (visibleColumns.name) row.push(item.name || "");
      if (visibleColumns.type) row.push(item.type || "");
      row.push(item.clientName || "");
      if (visibleColumns.startDate) row.push(item.startDate || "");
      if (visibleColumns.endDate) row.push(item.endDate || "");
      if (visibleColumns.renewalDate) row.push(item.renewalDate || "");
      if (visibleColumns.createdDate) row.push(item.createdDate || "");
      if (visibleColumns.status) row.push(item.status || "");
      if (visibleColumns.modified) row.push(item.modified || "");
      if (visibleColumns.contractType) row.push(item.contractType || "");
      if (visibleColumns.creationType) row.push(item.creationType || "");
      return row;
    });
    doc.autoTable({ head: [headers], body: rows, styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [255, 107, 53] } });
    doc.save("incomplete_contracts.pdf");
    setShowDownloadMenu(false);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['.doc', '.docx', '.pdf'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      setSnackbar({
        open: true,
        success: false,
        message: "Please select only .doc, .docx, or .pdf files"
      });
      return;
    }

    setLoaderMessage("Loading incomplete contracts...");
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('contract_title', file.name.split('.')[0]); // Use filename without extension as title
      formData.append('file', file);
      
      const response = await apiService.postData('contracts/upload-defaulter', formData, true);
      console.log('Upload defaulter contract response:', response);
      
      if (response && response.status === 'success') {
        await fetchData();
        setSnackbar({
          open: true,
          success: true,
          message: "Contract uploaded successfully to incomplete contracts"
        });
      } else {
        throw new Error(response?.message || 'Upload failed');
      }
    } catch (error) {
      console.error("Error uploading contract:", error);
      setSnackbar({
        open: true,
        success: false,
        message: error.message || "Error uploading contract"
      });
    } finally {
      setLoading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleMoveContract = async (rowData, targetStatus) => {
    try {
      const cleanId = rowData.id.replace("CONTRACT-", "");
      const userId = getUserId();
      
      if (targetStatus === "signed") {
        // Move contract to signed status and update hierarchy
        const payload = {
          contract_id: cleanId,
          status: "Signed",
          user_id: userId,
          is_wip: false,
          move_to_signed: true
        };
        
        const response = await apiService.postData("move-contract-to-signed", payload);
        
        if (response.status === "success") {
          setSnackbar({
            open: true,
            success: true,
            message: "Contract moved to signed contracts successfully"
          });
          // Refresh data to remove from incomplete contracts
          await fetchData();
        } else {
          setSnackbar({
            open: true,
            success: false,
            message: response.message || "Failed to move contract to signed"
          });
        }
      } else {
        // Keep as incomplete/draft
        const payload = {
          contract_id: cleanId,
          status: "Draft",
          user_id: userId
        };
        
        const response = await apiService.postData("update-contract-status", payload);
        
        if (response.status === "success") {
          setSnackbar({
            open: true,
            success: true,
            message: "Contract status updated successfully"
          });
          await fetchData();
        } else {
          setSnackbar({
            open: true,
            success: false,
            message: response.message || "Failed to update contract status"
          });
        }
      }
    } catch (error) {
      console.error("Error moving contract:", error);
      setSnackbar({
        open: true,
        success: false,
        message: "Error moving contract"
      });
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      <Sidebar setActiveSection={setActiveSection} activeSection={activeSection} />
      
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 20px' }}>
          <Breadcrumbs /><br/>
      <div style={{ background: '#fff', borderRadius: '8px', padding: '24px 28px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)', borderLeft: '4px solid #ff6b35', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ width: '28px', height: '28px', background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c61 100%)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '16px' }}>
            ⚠️
          </span>
          Incomplete Contracts
        </h1>
      </div>
      
      <Loader isLoading={loading} message={loaderMessage} />
      
      {!loading && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "20px", gap: "10px" }}>
            <input
              type="file"
              accept=".doc,.docx,.pdf"
              style={{ display: "none" }}
              id="upload-contract-input"
              onChange={handleFileUpload}
            />
            <button
              onClick={() => document.getElementById('upload-contract-input').click()}
              style={{
                background: "#ff6b35",
                color: "white",
                border: "1px solid #ff6b35",
                padding: "8px 14px",
                borderRadius: "6px",
                fontSize: "0.9rem",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              Upload Contract
            </button>
            <button
              onClick={() => setShowYearHierarchy(!showYearHierarchy)}
              style={{
                background: showYearHierarchy ? "#ff6b35" : "transparent",
                color: showYearHierarchy ? "white" : "#ff6b35",
                border: "1px solid #ff6b35",
                padding: "8px 14px",
                borderRadius: "6px",
                fontSize: "0.9rem",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              {showYearHierarchy ? "Back to Table View" : "Switch Year Hierarchy"}
            </button>
            {showYearHierarchy && (
              <div style={{ position: "relative" }} ref={yearDropdownRef}>
                <button
                  onClick={() => setShowYearDropdown(!showYearDropdown)}
                  style={{
                    background: "transparent",
                    color: "#ff6b35",
                    border: "1px solid #ff6b35",
                    padding: "8px 14px",
                    borderRadius: "6px",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  Search by Year
                  <span style={{ fontSize: "12px" }}>▼</span>
                </button>
                {showYearDropdown && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: "6px",
                    background: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    zIndex: 10,
                    minWidth: "200px",
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}>
                    {Object.entries(groupContractsByYear(filteredFlatData))
                      .sort(([a], [b]) => b.localeCompare(a))
                      .map(([year, contracts]) => (
                        <div
                          key={year}
                          onClick={() => {
                            setSelectedYear(year);
                            setExpandedYears({ [year]: true });
                            setShowYearDropdown(false);
                          }}
                          style={{
                            padding: "10px 16px",
                            cursor: "pointer",
                            borderBottom: "1px solid #f0f0f0",
                            background: selectedYear === year ? "#fff5f0" : "white",
                            color: selectedYear === year ? "#ff6b35" : "#333",
                            fontWeight: selectedYear === year ? "bold" : "normal",
                          }}
                          onMouseEnter={(e) => e.target.style.background = "#f8f9fa"}
                          onMouseLeave={(e) => e.target.style.background = selectedYear === year ? "#fff5f0" : "white"}
                        >
                          {year} ({contracts.length} contracts)
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
            <input
              type="text"
              placeholder="Search..."
              value={filters.global || ""}
              onChange={(e) => setFilters({ global: e.target.value })}
              style={{ padding: "8px", width: "170px", border: "1px solid #ff6b35", borderRadius: "4px", marginTop: "10px" }}
            />
            <button
              onClick={() => setShowColumnOptions(true)}
              style={{ cursor: "pointer", padding: "4px 12px", borderRadius: "6px", background: "transparent", color: "#4A4A4A", border: "1px solid #ff6b35", fontSize: "16px", fontWeight: 500 }}
            >
              ⚙ Column options
            </button>
            <div style={{ position: "relative" }} ref={downloadRef}>
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                style={{ cursor: "pointer", padding: "4px 12px", borderRadius: "6px", background: "transparent", border: "1px solid #ff6b35", color: "#4A4A4A", fontSize: "16px", fontWeight: 500, display: "flex", alignItems: "center", gap: "8px" }}
              >
                📥 Download
              </button>
              {showDownloadMenu && (
                <div style={{ position: "absolute", right: 0, marginTop: "6px", borderRadius: "6px", background: "white", border: "1px solid #e5e7eb", zIndex: 10 }}>
                  <button onClick={exportToExcel} style={{ cursor: "pointer", border: "none", display: "block", padding: "8px 12px", width: "120px", textAlign: "left", background: "none", color: "#4A4A4A" }}>
                    Download CSV
                  </button>
                  <button onClick={exportToPDF} style={{ cursor: "pointer", border: "none", display: "block", padding: "8px 12px", width: "120px", textAlign: "left", background: "none", color: "#4A4A4A" }}>
                    Download PDF
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div style={{ flex: 1, overflow: 'auto', marginBottom: '80px' }}>
            {showYearHierarchy ? (
              <div style={{ border: '1px solid #ff6b35', borderRadius: '4px', background: '#fff' }}>
                {Object.entries(groupContractsByYear(filteredFlatData)).filter(([year]) => year === selectedYear).map(([year, contracts]) => renderYearGroup(year, contracts))}
              </div>
            ) : (
              <DataTable
                value={filteredFlatData}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ width: '100%', border: '1px solid #ff6b35' }}
                emptyMessage="No incomplete contracts found"
                loading={loading}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
              >
              {visibleColumns.name && (
                <Column field="name" header="Contract Name" body={nameTemplate} sortable />
              )}
              <Column field="clientName" header="Client" sortable />
              {visibleColumns.vendor && (
                <Column field="vendorName" header="Vendor" sortable />
              )}
              {visibleColumns.contractType && (
                <Column field="contractType" header="Contract Type" sortable />
              )}
              {visibleColumns.status && (
                <Column field="status" header="Status" sortable />
              )}
              {visibleColumns.startDate && (
                <Column field="startDate" header="Start Date" sortable />
              )}
              {visibleColumns.createdDate && (
                <Column field="createdDate" header="Created Date" sortable />
              )}
              <Column header="Actions" style={{ width: '120px', textAlign: 'center' }} body={actionBodyTemplate} />
              </DataTable>
            )}
          </div>
          
          {renderEditModal()}

          {showColumnOptions && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(6px)", display: "flex", alignItems: "flex-start", justifyContent: "flex-end", zIndex: 1000 }}>
              <div style={{ width: "340px", background: "#fff", borderRadius: "8px", overflow: "hidden", marginTop: "100px", marginRight: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", fontWeight: "700", background: "#ff6b35", color: "#fff" }}>
                  <div>Column Options</div>
                  <button onClick={() => setShowColumnOptions(false)} aria-label="Close" style={{ background: "transparent", border: "none", fontSize: "25px", cursor: "pointer", color: "#fff" }}>×</button>
                </div>
                <div style={{ maxHeight: "50vh", overflowY: "auto", padding: "10px 12px" }}>
                  {["name", "type", "startDate", "createdDate", "status", "modified", "contractType", "creationType", "vendor"].map((key) => (
                    <ColumnOptionItem key={key} keyName={key} tempVisibleColumns={tempVisibleColumns} mandatoryColumns={mandatoryColumns} toggleColumn={toggleColumn} />
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "10px 12px", borderTop: "1px solid #e5e7eb" }}>
                  <button onClick={() => { setTempVisibleColumns(visibleColumns); setShowColumnOptions(false); }} style={{ background: "transparent", color: "#ff6b35", border: "1px solid #ff6b35", padding: "8px 14px", borderRadius: "6px" }}>Cancel</button>
                  <button onClick={() => { const newVisible = { ...tempVisibleColumns }; mandatoryColumns.forEach(col => { if (newVisible.hasOwnProperty(col)) newVisible[col] = true; }); setVisibleColumns(newVisible); localStorage.setItem('incompleteContractsVisibleColumns', JSON.stringify(newVisible)); setShowColumnOptions(false); }} style={{ background: "#ff6b35", color: "#fff", border: "1px solid #ff6b35", padding: "8px 14px", borderRadius: "6px" }}>Save</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      


          <Footer />

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            message={snackbar.message}
            sx={{
              '& .MuiSnackbarContent-root': {
                backgroundColor: snackbar.success ? '#4caf50' : '#f44336'
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default IncompleteContracts;